import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getExpensesCollectionRefs, getBorrowers } from 'selectors';
import { useSelector } from 'react-redux';

export const ScheduleTableContext = createContext();

export function useScheduleTable() {
  return useContext(ScheduleTableContext);
}

export function ScheduleTableProvider({ children, user, stateName, legalName, scheduleData, borrowers, ...otherProps }) {
  const [contacts, setContacts] = useState([]);
  const [searchButtonClicked, setSearchButtonClicked] = useState(false);
  const [uploadButtonClicked, setUploadButtonClicked] = useState(false);
  const [lastDataHash, setLastDataHash] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(true);
  const refs = useSelector(getExpensesCollectionRefs);

  const stateOptions = refs.statesCollection ? refs.statesCollection.map(state => ({
    value: state.value,
    label: state.label
  })) : [];

  const contactExists = useCallback((newContact, existingContacts) => {
    return existingContacts.some(existingContact => {
      const sameFullName = (existingContact.fullName || '').trim() === (newContact.fullName || '').trim();
      const sameState = (existingContact.state || '').trim() === (newContact.state || '').trim();
      const sameLlc = (existingContact.llc || '').trim() === (newContact.llc || '').trim();

      return sameFullName && sameState && sameLlc;
    });
  }, []);

  useEffect(() => {
    if (contacts.length === 0 && user) {
      const initialContact = {
        fullName: `${user.firstName} ${user.lastName}`,
        state: stateName.label || stateName,
        llc: legalName,
      };
      setContacts([initialContact]);
    }

    if (borrowers && borrowers.length > 1) {
      const borrowerContacts = borrowers.slice(1).map(borrower => ({
        fullName: borrower.borrower || '',
        state: borrower.state || '',
        llc: borrower.llc || '',
        stateCode: '',
      }));
      const uniqueBorrowerContacts = borrowerContacts.filter(newContact =>
        !contactExists(newContact, contacts)
      );

      if (uniqueBorrowerContacts.length > 0) {
        setContacts(prev => [...prev, ...uniqueBorrowerContacts]);
      }
    }
  }, [borrowers]);

  const addContact = useCallback(() => {
    setContacts(prev => [
      ...prev,
      { fullName: '', state: '', llc: '' }
    ]);
  }, []);

  const removeContact = useCallback((idx) => {
    if (idx === 0) return;
    setContacts(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateContact = useCallback((idx, field, value) => {
    setContacts(prev =>
      prev.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      )
    );
  }, []);

  const isContactValid = useCallback((contact) => {
    const hasFullName = contact.fullName && contact.fullName.trim().length > 0;
    const stateName = stateOptions.find(state => state.value === contact.stateCode) || {};
    const state = contact.state || stateName.label || '';
    const hasState = state && state.trim().length > 0;
    const hasLlc = contact.llc && contact.llc.trim().length > 0;
    const hasStateAndLlc = hasState && hasLlc;
    const hasNameAndState = hasFullName && hasState;

    return hasStateAndLlc || hasNameAndState;
  }, []);

  const areAllContactsValid = useMemo(() => {
    if (contacts.length === 0) return false;
    return contacts.every(contact => isContactValid(contact));
  }, [contacts, isContactValid]);

  const isSearchDisabled = useMemo(() => {
    return !areAllContactsValid;
  }, [areAllContactsValid]);

  const contextValue = {
    user,
    stateName,
    legalName,

    searchButtonClicked,
    setSearchButtonClicked,

    contacts,
    setContacts,
    addContact,
    removeContact,
    updateContact,

    isContactValid,
    areAllContactsValid,
    isSearchDisabled,

    uploadButtonClicked,
    setUploadButtonClicked,
    lastDataHash,
    setLastDataHash,
    isSubmitted,
    setIsSubmitted,

    ...otherProps
  };

  return (
    <ScheduleTableContext.Provider value={contextValue}>
      {children}
    </ScheduleTableContext.Provider>
  );
}

ScheduleTableProvider.propTypes = {
  children: PropTypes.node.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  stateName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ]),
  legalName: PropTypes.string,
};

ScheduleTableProvider.defaultProps = {
  user: null,
  stateName: '',
  legalName: '',
};
